import { useTranslation } from "react-i18next";
import { CreditCard, RotateCw, ScanLine } from "lucide-react";
import {
  KioskLoadingPanel,
  KioskStatePanel,
  type KioskStateAction,
} from "../../../../../components/kiosk-state-panel";
import { CardInsertAnimation } from "../../../components/CardInsertAnimation";
import type {
  CardReaderPhase,
  CardReaderReadContext,
} from "../../use-card-reader-session";

/**
 * The card-reader view a flow passes in (the subset of `useCardReaderSession`
 * the panel renders).
 *
 * @since 1.3.0
 * @example
 * ```ts
 * const view: KioskCardReaderView = useCardReaderSession(options);
 * ```
 */
export interface KioskCardReaderView {
  readonly phase: CardReaderPhase;
  readonly detectedCards: CardReaderReadContext["detectedCards"];
  readonly errorMessage: string | null;
  readonly readCard: () => void;
  readonly retry: () => void;
}

/**
 * Stable `data-testid`s of one flow's card panel.
 *
 * @since 1.3.0
 * @example
 * ```ts
 * const ids: KioskCardReaderTestIds = { panel: "scheduler-card-panel", readCard: "scheduler-read-card", retry: "scheduler-card-retry", readError: "scheduler-card-read-error" };
 * ```
 */
export interface KioskCardReaderTestIds {
  readonly panel: string;
  readonly readCard: string;
  readonly retry: string;
  readonly readError: string;
}

/**
 * Card-reader states shared by the scheduler and the prescription flow
 * (KO-07, KO-73, KO-84, SPK-04): one vocabulary ("Gesundheitskarte",
 * "Kartenleser"), content-sized state panels instead of a stretched hero, the
 * error tone for failures (no red text on blue), and a way to continue
 * without the card wherever the flow offers a name search. The card holder
 * number is never shown on the public screen.
 *
 * @since 1.3.0
 * @example
 * ```tsx
 * <KioskCardReaderPanel
 *   cardReader={cardReader}
 *   testIds={{ panel: "scheduler-card-panel", readCard: "scheduler-read-card", retry: "scheduler-card-retry", readError: "scheduler-card-read-error" }}
 *   searchAction={{ label: t("self-service.identification.card.without-card"), icon: Search, onClick: () => setMode("search"), testId: "scheduler-card-search-btn" }}
 * />
 * ```
 */
export function KioskCardReaderPanel({
  cardReader,
  testIds,
  searchAction,
}: {
  readonly cardReader: KioskCardReaderView;
  readonly testIds: KioskCardReaderTestIds;
  /** Continue with a name search; omit when the flow offers no search. */
  readonly searchAction?: KioskStateAction;
}) {
  const { t } = useTranslation();
  const { phase, detectedCards, errorMessage } = cardReader;
  const retryAction: KioskStateAction = {
    label: t("common.retry"),
    icon: RotateCw,
    onClick: cardReader.retry,
    testId: testIds.retry,
  };
  const helpBody = searchAction
    ? t("self-service.identification.card.help-with-search")
    : t("self-service.identification.card.help-reception");

  switch (phase) {
    case "loading":
      return (
        <KioskLoadingPanel
          testId={testIds.panel}
          title={t("self-service.identification.card.loading")}
        />
      );
    case "checking":
      return (
        <KioskLoadingPanel
          testId={testIds.panel}
          title={t("self-service.identification.card.checking")}
        />
      );
    case "reading":
      return (
        <KioskLoadingPanel
          testId={testIds.panel}
          title={t("self-service.identification.card.reading")}
          body={t("self-service.identification.card.reading-hint")}
        />
      );
    case "no-terminal":
      return (
        <KioskStatePanel
          tone="warning"
          testId={testIds.panel}
          icon={<CreditCard />}
          title={t("self-service.identification.card.no-terminal-title")}
          body={helpBody}
          action={searchAction}
        />
      );
    case "no-card":
      return (
        <KioskStatePanel
          tone="info"
          testId={testIds.panel}
          icon={<CreditCard />}
          title={t("self-service.identification.card.insert-title")}
          body={t("self-service.identification.card.insert-detail")}
          secondaryAction={searchAction}
        >
          <CardInsertAnimation />
        </KioskStatePanel>
      );
    case "card-unreadable":
      return (
        <KioskStatePanel
          tone="error"
          testId={testIds.readError}
          icon={<CreditCard />}
          title={t("self-service.identification.card.unreadable-title")}
          body={helpBody}
          action={retryAction}
          secondaryAction={searchAction}
        />
      );
    case "error":
      return (
        <KioskStatePanel
          tone="error"
          testId={testIds.readError}
          icon={<CreditCard />}
          title={t("self-service.identification.card.error-title")}
          body={errorMessage ?? helpBody}
          action={retryAction}
          secondaryAction={searchAction}
        />
      );
    case "card-detected": {
      if (errorMessage !== null) {
        return (
          <KioskStatePanel
            tone="error"
            testId={testIds.readError}
            icon={<CreditCard />}
            title={t("self-service.identification.card.error-title")}
            body={errorMessage}
            action={retryAction}
            secondaryAction={searchAction}
          />
        );
      }
      const holder = detectedCards[0]?.CardHolderName;
      return (
        <KioskStatePanel
          tone="success"
          testId={testIds.panel}
          icon={<CreditCard />}
          title={t("self-service.identification.card.detected")}
          body={holder}
          action={{
            label: t("self-service.identification.card.read"),
            icon: ScanLine,
            onClick: cardReader.readCard,
            testId: testIds.readCard,
          }}
        />
      );
    }
  }
}
